// ABOUTME: Date picker component with custom calendar matching CDateRangePicker style
// ABOUTME: Supports single date selection with locale support and mobile-friendly popover

import type { FC } from "react"
import React, { useState, useRef, useEffect } from "react"
import { createPortal } from "react-dom"
import { format, isSameDay, isBefore, isAfter, startOfDay, isValid } from "date-fns"
import { useTranslation } from "react-i18next"
import { CalendarIcon, X, ChevronLeft, ChevronRight } from "lucide-react"
import { CalendarMonthGrid } from "./CalendarMonthGrid"
import { useCalendarLabels } from "./useCalendarLabels"
import useDevice from "@shared/hooks/useDevice"
import { useZIndex } from "@shared/hooks/useZIndex"

type WidthMode = "single" | "range" | "full"

export interface CDatePickerProps {
  selected?: Date | null
  onChange?: (date: Date | null) => void
  inline?: boolean
  minDate?: Date
  maxDate?: Date
  /** Which month/year the calendar opens on when there is no selection (clamped to min/max). */
  defaultViewDate?: Date
  disabled?: boolean
  placeholderText?: string
  dateFormat?: string
  className?: string
  widthMode?: WidthMode
  showYearDropdown?: boolean
  showMonthDropdown?: boolean
  customInput?: React.ReactNode
  required?: boolean
  isClearable?: boolean
}

const widthClasses: Record<WidthMode, string> = {
  single: "w-[140px] max-md:w-full",
  range: "w-[230px] max-md:w-full",
  full: "w-full",
}

const generateYears = (minYear?: number, maxYear?: number): number[] => {
  const currentYear = new Date().getFullYear()
  const start = minYear ?? 1920
  const end = maxYear ?? currentYear + 10
  const years: number[] = []
  for (let year = start; year <= end; year++) {
    years.push(year)
  }
  return years
}

interface CalendarGridProps {
  month: Date
  selected: Date | null
  onDateClick: (date: Date) => void
  weekdays: string[]
  minDate?: Date
  maxDate?: Date
}

const CalendarGrid: FC<CalendarGridProps> = ({ month, selected, onDateClick, weekdays, minDate, maxDate }) => {
  // Compare dates without time to avoid issues with time zones
  const isDisabled = (date: Date) => {
    const dateOnly = startOfDay(date)
    if (minDate && isBefore(dateOnly, startOfDay(minDate))) return true
    if (maxDate && isAfter(dateOnly, startOfDay(maxDate))) return true
    return false
  }

  return (
    <CalendarMonthGrid
      month={month}
      weekdays={weekdays}
      dayState={(date) => ({
        disabled: isDisabled(date),
        highlighted: Boolean(selected && isValid(selected) && isSameDay(date, selected)),
      })}
      onDateClick={onDateClick}
    />
  )
}

export const CDatePicker: FC<CDatePickerProps> = ({
  selected,
  onChange,
  inline = false,
  minDate,
  maxDate,
  defaultViewDate,
  disabled = false,
  placeholderText,
  dateFormat = "dd.MM.yyyy",
  className = "",
  widthMode = "single",
  customInput,
  isClearable = false,
}) => {
  const { t } = useTranslation()
  const { isMobile } = useDevice()
  const [isOpen, setIsOpen] = useState(false)
  // Open on the selection, else the caller's default view, else today — but never outside
  // [minDate, maxDate], so a range that excludes today (e.g. a birthday picker) doesn't fall
  // back to the earliest allowed year in the year dropdown.
  const [currentMonth, setCurrentMonth] = useState<Date>(() => {
    const initial = selected ?? defaultViewDate ?? new Date()
    if (minDate && isBefore(initial, minDate)) return minDate
    if (maxDate && isAfter(initial, maxDate)) return maxDate
    return initial
  })
  const containerRef = useRef<HTMLDivElement>(null)
  const zIndex = useZIndex(isOpen)

  const { weekdays, monthLabels } = useCalendarLabels()
  const placeholder = placeholderText ?? t("datepicker.placeholder")
  const widthClass = widthClasses[widthMode]

  const minYear = minDate ? minDate.getFullYear() : 1920
  const maxYear = maxDate ? maxDate.getFullYear() : new Date().getFullYear() + 10
  const YEARS = generateYears(minYear, maxYear)

  const handleSelect = (date: Date) => {
    onChange?.(date)
    if (!inline) {
      setIsOpen(false)
    }
  }

  const formatDate = (date: Date | null | undefined): string => {
    if (!date || !isValid(date)) return placeholder
    return format(date, dateFormat)
  }

  // Sync currentMonth with selected when it changes externally — in the render that sees the change, so the grid
  // never paints a frame of the previous month.
  const [monthSyncedFor, setMonthSyncedFor] = useState({ selected, isOpen })
  if (monthSyncedFor.selected !== selected || monthSyncedFor.isOpen !== isOpen) {
    setMonthSyncedFor({ selected, isOpen })
    if (selected && isValid(selected) && !isOpen) {
      setCurrentMonth(selected)
    }
  }

  // Navigation handlers
  const goToPrevMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))
  const goToNextMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))

  // Close on Escape
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false)
      }
    }
    if (isOpen && !inline) {
      document.addEventListener("keydown", handleEscape)
      return () => document.removeEventListener("keydown", handleEscape)
    }
  }, [isOpen, inline])

  const calendarContent = (
    <div className="p-4 bg-white">
      {/* Navigation header */}
      <div className="flex items-center justify-between mb-3 px-1">
        {/* Month navigation with arrows around month only */}
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={goToPrevMonth}
            className="p-1 hover:bg-gray-100 rounded cursor-pointer"
          >
            <ChevronLeft className="w-4 h-4 text-gray-500" />
          </button>
          <select
            value={currentMonth.getMonth()}
            onChange={(e) => setCurrentMonth(new Date(currentMonth.getFullYear(), parseInt(e.target.value, 10), 1))}
            className="text-sm font-medium bg-transparent border-none cursor-pointer focus:ring-0 p-0 pr-4 appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3csvg%20xmlns%3d%22http%3a%2f%2fwww.w3.org%2f2000%2fsvg%22%20width%3d%2210%22%20height%3d%2210%22%20viewBox%3d%220%200%2012%2012%22%3e%3cpath%20fill%3d%22%236b7280%22%20d%3d%22M2%204l4%204%204-4%22%2f%3e%3c%2fsvg%3e')] bg-no-repeat bg-[position:right_0.25rem_center]"
          >
            {monthLabels.map((month, index) => (
              <option
                key={index}
                value={index}
              >
                {month}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={goToNextMonth}
            className="p-1 hover:bg-gray-100 rounded cursor-pointer"
          >
            <ChevronRight className="w-4 h-4 text-gray-500" />
          </button>
        </div>
        {/* Year dropdown */}
        <select
          value={currentMonth.getFullYear()}
          onChange={(e) => setCurrentMonth(new Date(parseInt(e.target.value, 10), currentMonth.getMonth(), 1))}
          className="text-sm font-medium bg-transparent border-none cursor-pointer focus:ring-0 p-0 pr-4 appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3csvg%20xmlns%3d%22http%3a%2f%2fwww.w3.org%2f2000%2fsvg%22%20width%3d%2210%22%20height%3d%2210%22%20viewBox%3d%220%200%2012%2012%22%3e%3cpath%20fill%3d%22%236b7280%22%20d%3d%22M2%204l4%204%204-4%22%2f%3e%3c%2fsvg%3e')] bg-no-repeat bg-[position:right_0.25rem_center]"
        >
          {YEARS.map((year) => (
            <option
              key={year}
              value={year}
            >
              {year}
            </option>
          ))}
        </select>
      </div>

      {/* Calendar grid */}
      <CalendarGrid
        month={currentMonth}
        selected={selected ?? null}
        onDateClick={handleSelect}
        weekdays={weekdays}
        minDate={minDate}
        maxDate={maxDate}
      />

      {/* Today button at bottom */}
      <div className="mt-3 pt-3 border-t border-gray-200">
        <button
          type="button"
          onClick={() => {
            const now = new Date()
            // Use noon (12:00) to avoid timezone issues when converting to UTC
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12, 0, 0)
            setCurrentMonth(today)
            handleSelect(today)
          }}
          className="px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors cursor-pointer"
        >
          {t("calendar.today")}
        </button>
      </div>
    </div>
  )

  // Inline mode - just render the calendar
  if (inline) {
    return (
      <div className={`${widthClass} ${className}`}>
        <div className="border border-gray-200 rounded-lg">{calendarContent}</div>
      </div>
    )
  }

  // Popover mode
  return (
    <div
      ref={containerRef}
      className={`relative ${customInput ? "" : widthClass} ${className}`}
    >
      {/* Trigger - custom input or default button */}
      {customInput ? (
        // The click goes to the caller's own control rather than to a wrapper, so the keyboard
        // reaches it the way it reaches any button.
        React.cloneElement(customInput as React.ReactElement<{ value?: string; onClick?: () => void }>, {
          value: formatDate(selected),
          onClick: () => !disabled && setIsOpen(!isOpen),
        })
      ) : (
        <div className="relative flex items-center">
          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            disabled={disabled}
            className="flex items-center gap-2 px-3 py-1.5 h-[30px] text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          >
            <CalendarIcon className="w-4 h-4 text-gray-500 shrink-0" />
            <span className="truncate text-left flex-1">{formatDate(selected)}</span>
          </button>
          {isClearable && selected && !disabled && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                onChange?.(null)
              }}
              className="absolute right-2 p-0.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
      )}

      {/* Dropdown - rendered via portal to escape stacking context */}
      {isOpen &&
        zIndex > 0 &&
        createPortal(
          <div
            // Not a control: the backdrop only repeats with the mouse what Escape already does.
            role="presentation"
            data-date-picker-portal="true"
            className="fixed inset-0"
            style={{ zIndex }}
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) {
                setIsOpen(false)
              }
            }}
          >
            <div
              data-date-picker-portal="true"
              className="fixed bg-white border border-gray-200 rounded-xl shadow-lg overflow-auto max-h-[90vh] w-auto"
              style={{
                zIndex: zIndex + 1,
                ...(() => {
                  if (isMobile) {
                    return { top: 60, left: "50%", transform: "translateX(-50%)" }
                  }
                  if (!containerRef.current) {
                    return { top: 100, left: 0 }
                  }
                  const rect = containerRef.current.getBoundingClientRect()
                  const calendarWidth = 300
                  const calendarHeight = 380
                  const padding = 8

                  // Calculate vertical position
                  const spaceBelow = window.innerHeight - rect.bottom
                  const spaceAbove = rect.top
                  const top =
                    spaceBelow >= calendarHeight + padding
                      ? rect.bottom + 4
                      : spaceAbove >= calendarHeight + padding
                        ? rect.top - calendarHeight - 4
                        : padding

                  // Calculate horizontal position
                  const spaceRight = window.innerWidth - rect.left
                  const left =
                    spaceRight >= calendarWidth + padding
                      ? rect.left
                      : Math.max(padding, window.innerWidth - calendarWidth - padding)

                  return { top, left }
                })(),
              }}
            >
              {calendarContent}
            </div>
          </div>,
          document.body,
        )}
    </div>
  )
}
